import { Service, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, defer, shareReplay, switchMap, tap } from 'rxjs';

import { AppLang, LangStore } from '../core/lang.service';
import { CatalogApi } from './api/catalog.api';
import type { Banner } from './models/catalog/banner.model';
import type { Catalog } from './models/catalog/catalog.model';

/** Один запрос на сессию: каталог нужен Главной, /cart и /checkout, а за визит он не меняется. */
@Service()
export class CatalogCache {
  private readonly api = inject(CatalogApi);

  /** Объявлен раньше кэшируемых полей: инициализаторы выполняются по порядку, `cached()` пишет сюда. */
  private readonly resets: (() => void)[] = [];

  /** Позиции загружены хотя бы раз — гейт акций; смена языка и invalidate() его не сбрасывают. */
  private readonly readyState = signal(false);
  readonly ready = this.readyState.asReadonly();

  /** Кэш на язык: возврат к уже виденному языку не ходит в сеть. */
  private readonly catalogByLang = new Map<AppLang, Observable<Catalog>>();
  private readonly bannersByLang = new Map<AppLang, Observable<Banner[]>>();

  private readonly lang$ = toObservable(inject(LangStore).lang);

  /** switchMap по языку: живые подписчики получают данные нового языка без ре-подписки. */
  readonly catalog$: Observable<Catalog> = this.lang$.pipe(
    switchMap((lang) =>
      this.forLang(this.catalogByLang, lang, (l) =>
        // tap до shareReplay: флаг ставит реальный ответ, а не реплей буфера новым подписчикам.
        this.api.getCatalog(l).pipe(tap(() => this.readyState.set(true))),
      ),
    ),
  );
  readonly banners$: Observable<Banner[]> = this.lang$.pipe(
    switchMap((lang) => this.forLang(this.bannersByLang, lang, (l) => this.api.getBanners(l))),
  );

  private forLang<T>(
    cache: Map<AppLang, Observable<T>>,
    lang: AppLang,
    load: (lang: AppLang) => Observable<T>,
  ): Observable<T> {
    let stream = cache.get(lang);
    if (stream === undefined) {
      stream = this.cached(() => load(lang));
      cache.set(lang, stream);
    }
    return stream;
  }

  invalidate(): void {
    for (const reset of this.resets) {
      reset();
    }
  }

  /** Кэшируется сам поток, а не значение: подписчик во время полёта запроса присоединяется к нему же. */
  private cached<T>(load: () => Observable<T>): Observable<T> {
    let request: Observable<T> | null = null;

    this.resets.push(() => {
      request = null;
    });

    return defer(() => {
      if (request !== null) {
        return request;
      }

      request = load().pipe(
        // Ошибку shareReplay не кэширует (resetOnError в RxJS 7): следующий подписчик снова идёт в сеть.
        // refCount: false — буфер переживает уход всех подписчиков: возврат на Главную не идёт в сеть.
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      return request;
    });
  }
}
